/**
 * This file is part of an epic that is currently on hold and is not being used in the application.
 * Please refer to the README.md file in the root of the PDFRenderer directory for more information.
 */
import { Link, Text, TextProps } from "@react-pdf/renderer";
import linkifyit from "linkify-it";
import React from "react";

import { COLORS, StylesFn, usePdfStyles } from "../../styles";

type TextWithFormattingProps = {
	children: React.ReactNode;
} & TextProps;

const linkify = new linkifyit();

const textWithFormattingStyles: StylesFn = () => ({
	link: {
		color: COLORS.primary[5],
		textDecoration: "none",
	},
});

/**
 * A wrapper around the react-pdf Text component that adds support for preserving
 * whitespace and automatically detecting and linking URLs.
 */
export const TextWithFormatting = ({ children, ...props }: TextWithFormattingProps) => {
	const styles = usePdfStyles(textWithFormattingStyles);

	if (typeof children !== "string") {
		return <Text {...props}>{children}</Text>;
	}

	// Split the text by newline characters
	const lines = children.split("\n");

	// All we are doing here is creating an array of characters from the text.
	// In doing this, and passing the array to the Text component, we can preserve
	// the whitespace in the text. (This is most certainly a hack, but I couldn't find another
	// way to preserve whitespace. If you know of a better way, please replace this implementation.)
	const textElements: React.ReactNode[] = lines.flatMap((line, index) => {
		const lineElements = line.split("");
		if (index < lines.length - 1) {
			lineElements.push("\n");
		}
		return lineElements;
	});

	// Detect any URLs in the text
	const linkMatches = linkify.match(textElements.join(""));

	// If there are no links, we're done here. Just render the text.
	if (!linkMatches) {
		return <Text {...props}>{textElements}</Text>;
	}

	// If there are links, we're going to loop through the matches and create Link components.
	const textAndLinkElements: React.ReactNode[] = [];
	let endOfLastLinkIndex = 0;

	for (const match of linkMatches) {
		// Add the text that comes before the link
		textAndLinkElements.push(textElements.slice(endOfLastLinkIndex, match.index));

		// Add the link
		textAndLinkElements.push(
			<Link key={`${match.index}-${match.lastIndex}`} src={match.url} style={styles.link}>
				{match.text}
			</Link>
		);

		// Update the end of the last link index
		endOfLastLinkIndex = match.lastIndex;
	}

	// Add the text that comes after the last link
	textAndLinkElements.push(textElements.slice(endOfLastLinkIndex));

	return <Text {...props}>{textAndLinkElements}</Text>;
};
